import React, {FC, useEffect, useState} from 'react';

import {t} from "i18next";
import { v4 as uuidv4 } from 'uuid';
import {createSelector} from "reselect";
import {RootState} from "../../../../redux/store";
import {useAppSelector} from "../../../../redux/hooks";
import FormSelect, {ISelectOption} from "../../../commons/Select/FormSelect";
import warehousesService from "../../../../api/services/warehousesService";
import {warehouseTypeLabels} from "../../../Tables/WarehouseTable/parse-warehouse-type";
import FormMultiSelect from "../FormMultiSelect";

interface WarehouseSelectorProps {
  data: string|string[]|null
  placeholder?: string|null
  label?: string|null|false
  customLabelClassName?: string
  isMultiple?: boolean
  onChange: (selected: string|string[]) => void
  validationError?: boolean,
  validationErrorDescription?: string,
  filterFunc?: (v:any) => true,
}

const memoizedSelector = createSelector(
    (state: RootState) => state.sellers.currentSeller,
    (currentSeller) => ({ currentSeller })
);

export const WarehouseSelector: FC<WarehouseSelectorProps> = ({
  data,
  placeholder ,
  label ,
  isMultiple,
  customLabelClassName,
  onChange,
  validationError,
  validationErrorDescription,
  filterFunc
}) => {

  const { currentSeller } = useAppSelector(memoizedSelector)

  const [fieldId] = useState("warehouse_selector_" + uuidv4());
  const [warehouses, setWarehouses] = useState<{uuid: string, status: string, name: string, type: string, externalService: string, externalID: string}[]>([]);
  const [updateWarehouseSelectorOptions, setUpdateWarehouseSelectorOptions] = useState<ISelectOption[]>([]);
  const [selected, setSelected] = useState<string|string[]>('');
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

  useEffect(() => {
    const currentSellerId = (currentSeller?.info?.data?.id || '').toString();
    // Warehouses
    currentSellerId && warehousesService.getWarehousesForSeller(parseInt(currentSellerId))
        .then(
            (res) => {
              if (res.data.success) {
                // Фильтруем неактивные склады и исключаем непубличные склады
                setWarehouses(res.data.data.filter(filterFunc))
              }
            },
            (err) => {
              alert(err.response.data.error);
            }
        )
        .catch((err) => {
          alert(err.message);
        })
    ;
  }, [])

  useEffect(() => {
    if (!data) {
      return
    }

    // @ts-ignore
    setSelected(data)

  }, [data])

  useEffect(() => {
    // Warehouses
    const selectOptions: ISelectOption[] = [
      {text: placeholder || '', value: ''}
    ]
    warehouses.forEach(w => {
      const typeLabel = t(warehouseTypeLabels[w.type]) ?? ''
      let name = w.name
      let extID = ''
      if (w.externalService) {
        const lowerName = name.toLowerCase()
        if (w.externalService !== 'SystemPublicWH') {
          if (w.externalService.indexOf('LK') !== -1) {
            extID = `, ${w.externalID}`
          } else if (lowerName.indexOf(w.externalService.toLowerCase()) === -1) {
            name = t<string>(`Data source ${w.externalService}`, {ns: "messages"}) + ` ${name}`
            extID = `, ${w.externalID}`
          }
        }
      }

      selectOptions.push({text: `${name} (${typeLabel}${extID})`, value: w.uuid})
    })
    setUpdateWarehouseSelectorOptions(selectOptions)
  }, [warehouses])

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    onChange(selected)
  }, [selected])

  const customInputClassName = 'border-[#E2E8F0] mb-1 py-2 rounded-md text-sm shadow-sm placeholder-slate-400  w-full focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none'

  function updateSelected(e: any): void {
    setSelected(e.target.value)
  }

  return (
      <>
        {!isMultiple ?
            <FormSelect
                id={fieldId}
                name={fieldId}
                value={selected}
                label={label}
                changeHandler={updateSelected}
                customClassName={customInputClassName}
                customLabelClassName={customLabelClassName}
                blurHandler={updateSelected}
                validationError={validationError}
                validationErrorDescription={validationErrorDescription}
                options={updateWarehouseSelectorOptions}
                require={false}
            ></FormSelect>
            :
            <FormMultiSelect
                placeholder={(placeholder || '')}
                id={fieldId}
                name={fieldId}
                // @ts-ignore
                values={selected}
                label={label}
                // @ts-ignore
                changeHandler={values => setSelected(values)}
                customClassName={customInputClassName}
                customLabelClassName={customLabelClassName}
                validationError={validationError}
                validationErrorDescription={validationErrorDescription}
                options={updateWarehouseSelectorOptions}
                require={false}
            ></FormMultiSelect>
        }
      </>
  );
};
